import type { Entities_Player_UpdateProgress_Request } from '@suntex.fim/api-client';

/**
 * Module-progress payloads for the DOM boards.
 *
 * The canvas boards reach the same two endpoints through `GameFrame`
 * (`framework/GameFrame.ts:285-293` to read, `:492-519` to write). A DOM board
 * has no `GameFrame`, so it has to talk to them itself - and it must, because
 * the VIF stage machinery downstream depends on the `GameRecord` those writes
 * produce. `UpdateVIFStage` walks back to the previous game and, finding no
 * completed record, rolls the player's stage backwards.
 *
 * Nothing here touches React or the network, so the payload rules are unit
 * testable against the server's validation.
 */

/**
 * Legacy `GameFrame.DEFAULT_SCORE_MAX`. `boardGameEngine.gameOver` sets
 * `score = scoreMax` before ending the game and no VIF module changes the max,
 * so a won board always posts exactly this (`:375-384`).
 */
export const VIF_WIN_SCORE = 360;

/**
 * Legacy `GameFrame.endGame` does `bonus = Math.round(bonus + 0.4)` over a
 * bonus these games never increment, which is always 0 (`:342`).
 */
export const VIF_BONUS = 0;

/** The player's position in a module, as the server reports it. */
export interface ModulePosition {
  /** Legacy `gameFrame.round`. The server's *next* round. */
  round: number;
  /** Legacy `gameFrame.game`. The server's *next* game. */
  game: number;
}

export interface PracticeCheck extends ModulePosition {
  finishedEarning: boolean;
  playerId: number | undefined;
  roundsInModule: number;
  gamesPerRound: number;
}

/**
 * Whether a loaded position can be played for score, or only for practice.
 *
 * Ported from `GameFrame.onVarsLoad` (`:296-326`), which flips to preview mode
 * when the player has finished earning, when there is no usable player, or
 * when the stored position falls outside the variant's own bounds. A failed
 * fetch takes the same branch, so callers should treat an error as practice.
 */
export function isPracticeOnly({
  round,
  game,
  finishedEarning,
  playerId,
  roundsInModule,
  gamesPerRound,
}: PracticeCheck): boolean {
  return (
    finishedEarning ||
    round < 1 ||
    !playerId ||
    playerId < 1 ||
    game > gamesPerRound ||
    round > roundsInModule
  );
}

export interface BuildUpdateArgs extends ModulePosition {
  playerId: number;
  moduleId: number;
  moduleVariant: number;
  /** Round-tripped from the progress response, as `GameFrame` does. */
  extendedData: string;
  /** Seconds of measured activity since the last post. */
  timeOnTask: number;
}

/**
 * The unsigned body for `POST /Player/UpdateProgress`.
 *
 * Field-for-field what `GameFrame.updateDatabase` sends (`:499-510`), which
 * matters twice over: the server rejects the call outright unless `round` and
 * `game` equal the `NextRound`/`NextGame` it last reported, and the signature
 * is computed across every property, so an extra or missing field invalidates
 * the hash.
 *
 * `salt` and `hash` are left empty for `hashUpdateRequest` to fill in.
 */
export function buildUpdateProgressRequest({
  playerId,
  moduleId,
  moduleVariant,
  round,
  game,
  extendedData,
  timeOnTask,
}: BuildUpdateArgs): Entities_Player_UpdateProgress_Request {
  return {
    playerId,
    salt: '',
    hash: '',
    moduleId,
    moduleVariant,
    round,
    game,
    advanceProgress: true,
    score: VIF_WIN_SCORE,
    bonus: VIF_BONUS,
    extendedData,
    timeOnTask,
  } as Entities_Player_UpdateProgress_Request;
}

/**
 * The next position in practice mode, where nothing is posted and the counters
 * advance locally. Ported from `GameFrame.nextGame`'s preview branch
 * (`:464-481`); `round` past the end returns 0, which the caller shows as the
 * practice menu rather than a fifth round.
 */
export function nextPracticePosition(
  { round, game }: ModulePosition,
  roundsInModule: number,
  gamesPerRound: number,
): ModulePosition {
  if (game < gamesPerRound) {
    return { round, game: game + 1 };
  }

  const nextRound = round + 1;

  return nextRound > roundsInModule
    ? { round: 0, game: 1 }
    : { round: nextRound, game: 1 };
}
